// Edit a queued (not yet sent) message in place. Save keeps its place in line;
// Cancel restores the original words. Esc cancels, Ctrl/Cmd+Enter saves.
import { useEffect, useRef, useState } from "react";

export function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}
export function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}

/** Seconds left on an edit window, ticking once a second while one is open. */
export function useSecondsLeft(until?: number): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until || until <= Date.now()) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  if (!until) return null;
  const left = Math.ceil((until - now) / 1000);
  return left > 0 ? left : null;
}

export default function QueuedEditor({ initial, busy, onSave, onCancel }: {
  initial: string;
  busy: boolean;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [text]);
  const unchanged = text === initial;
  const save = () => { if (!busy) unchanged ? onCancel() : onSave(text); };
  return (
    <div className="pendingQueue__editor">
      <textarea ref={ref} className="pendingQueue__editText" value={text} rows={2} disabled={busy}
        aria-label="Edit queued message"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCancel(); }
          else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
        }} />
      <div className="pendingQueue__actions">
        <button type="button" className="pendingQueue__sendNow pendingQueue__editCancel" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="pendingQueue__sendNow pendingQueue__editSave" disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
